/**
 * A resposta que o THREAD guarda para um cartão de onboarding.
 *
 * ★ [owner, 21/09/2026] *"os botões de pular / enviar respostas / continuar
 * deveriam sumir; no aside o botão continua lá; as respostas só ficam dentro
 * do card renderizado no chat, no aside como artefato não"*. O cartão guardava
 * em `data` o que a pessoa respondeu e se a etapa foi concluída — e `data` é
 * por INSTÂNCIA: a cópia do aside nascia sem saber de nada, e um F5 apagava
 * tudo. O thread, esse, sobrevive — a resposta já está lá, como a mensagem
 * seguinte da pessoa, no formato que o servidor reconhece. Esta função é a
 * fonte ÚNICA para as duas superfícies (a bolha e o aside): as duas leem o
 * mesmo `messages` e chegam à mesma resposta.
 *
 * O que conta como resposta ao cartão é o que o SERVIDOR aceita para avançar
 * a apresentação (`core/onboarding/falas.py`, espelhado por
 * `ehFalaDoCartaoDeOnboarding`): "Continuar", "Pular esta etapa", as
 * respostas da etapa 1 e a escolha da etapa 2 — mais o atalho da etapa 5,
 * cujo "Rodar" vira a fala do comando (`/panorama`). Fala livre no meio da
 * apresentação NÃO é resposta: o servidor a manda ao modelo e deixa a etapa
 * pendente para os botões, então os botões continuam.
 *
 * ⚠️ A varredura começa na mensagem SEGUINTE à do cartão e PARA na próxima
 * que carregue um cartão de onboarding: quem respondeu depois de uma fala
 * livre ("oi" → resposta do modelo → "Continuar") ainda é reconhecido, e a
 * resposta da etapa seguinte nunca é atribuída à anterior. A mensagem da
 * boas-vindas carrega a etapa 1 no mesmo turno — a varredura dela começa na
 * mensagem seguinte, então o "Continuar" da boas-vindas responde às duas, que
 * é exatamente o que o servidor faz com ele.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 */
import { normalizeText } from '@utils/Format'
import { ehCartaoDeOnboarding } from '@modules/aawz-intelligence-v2/ui-registry/chave-do-onboarding'
import { ehFalaDoCartaoDeOnboarding } from '@modules/aawz-intelligence-v2/utils/falas-do-onboarding'

const PAPEL_USUARIO = 'user'
const BARRA_DO_COMANDO = '/'
/** O que pode vir logo depois do comando: espaço (argumento) ou o fim. */
const FIM_DO_COMANDO = /^(\s|$)/

function carregaCartaoDeOnboarding(mensagem) {
  const chamadas = (mensagem && mensagem.tool_calls) || []
  return chamadas.some(ehCartaoDeOnboarding)
}

/** O slug do atalho da etapa 5, quando a mensagem carrega essa etapa. */
function atalhoDoCartao(mensagem) {
  const chamadas = (mensagem && mensagem.tool_calls) || []
  const parte = chamadas.find(ehCartaoDeOnboarding)
  const props = parte && parte.ui && parte.ui.props
  return (props && typeof props.atalho === 'string' && props.atalho) || ''
}

/**
 * "Rodar /panorama" vira a fala do comando — é ela que fecha a etapa 5.
 *
 * ★ O que fica no thread é o LABEL do catálogo (`/pré-reunião`,
 * `/diagnóstico-fee` — `core/atalhos/catalogo.py`), e o `atalho` do cartão é
 * o SLUG, sem acento (`pre-reuniao`). Comparar sem `normalizeText` reconhece
 * `panorama` hoje e deixaria a etapa 5 sem resposta no dia em que a
 * demonstração for um atalho acentuado. E o comando termina em espaço ou no
 * fim: `/panoramax` não é `/panorama`.
 */
function ehFalaDoAtalho(texto, atalho) {
  if (!atalho) return false
  const fala = normalizeText(texto)
  const comando = BARRA_DO_COMANDO + normalizeText(atalho)
  if (fala.indexOf(comando) !== 0) return false
  return FIM_DO_COMANDO.test(fala.slice(comando.length))
}

/** O texto da mensagem da PESSOA quando ela responde ao cartão; senão `''`. */
function respostaNaMensagem(mensagem, atalho) {
  if (!mensagem || mensagem.role !== PAPEL_USUARIO) return ''
  const texto = typeof mensagem.content === 'string' ? mensagem.content : ''
  if (ehFalaDoCartaoDeOnboarding(texto)) return texto
  return ehFalaDoAtalho(texto, atalho) ? texto : ''
}

/**
 * @param {Array} mensagens `messages` do store.
 * @param {?string} idDaMensagem O `id` da mensagem que carrega o cartão.
 * @returns {string} A fala com que a pessoa respondeu, ou `''`.
 * @example respostaAoCartaoDeOnboarding([], 'm-1') // → ''
 */
export function respostaAoCartaoDeOnboarding(mensagens, idDaMensagem) {
  const lista = Array.isArray(mensagens) ? mensagens : []
  if (!idDaMensagem) return ''
  const indice = lista.findIndex((m) => m && m.id === idDaMensagem)
  // A pré-condição mora AQUI, não no chamador: o aside chama para qualquer
  // cartão de tool, e "a mensagem de origem carrega um cartão de onboarding"
  // é o que dá sentido à varredura (review #2364).
  if (indice === -1 || !carregaCartaoDeOnboarding(lista[indice])) return ''
  const atalho = atalhoDoCartao(lista[indice])
  for (let i = indice + 1; i < lista.length; i += 1) {
    const mensagem = lista[i]
    // A etapa seguinte fecha a janela: a resposta dela nunca é desta.
    if (carregaCartaoDeOnboarding(mensagem)) return ''
    const resposta = respostaNaMensagem(mensagem, atalho)
    if (resposta) return resposta
  }
  return ''
}
